// this hook gives each route its own title / description / canonical url.
//
// why it's needed: the whole site is one html file (public/index.html) that
// react swaps content into. without this, every page (sign in, privacy, terms)
// shares the homepage's title and description. google would see five pages
// all claiming to be the same thing, which is exactly the "duplicate title"
// warning that shows up in search console.
//
// this only helps crawlers that run javascript though (google does, most others
// don't). the blog articles under public/blog/ are plain html files for that
// reason - their tags are in the source before any js runs.
import { useEffect } from 'react';

// every url on the site lives under this. one constant so switching to a custom
// domain later is a single edit here (and in robots.txt / sitemap.xml).
export const SITE_ORIGIN = 'https://gptdisguise.app';

// find a <meta> tag by attribute, or make one if the page doesn't have it yet.
// index.html already ships most of these; the ones it doesn't get created on
// the fly and then reused on every later route change.
function upsertMeta(attr, key, content) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

// title is what shows in the browser tab and as the search result headline,
// description is the grey blurb under it, path is the route (e.g. '/privacy')
// which we turn into an absolute canonical url, and noIndex is for pages that
// should never show up in search at all.
export function usePageMeta({ title, description, path, noIndex = false }) {
  useEffect(() => {
    document.title = title;

    upsertMeta('name', 'description', description);

    // canonical tells google which url is the "real" one. without it, the same
    // page reached via ?utm_source=... or a trailing slash can be treated as a
    // separate, competing copy.
    const canonicalUrl = `${SITE_ORIGIN}${path}`;
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // keep the social preview in step with the page, otherwise every shared
    // link shows the homepage's title regardless of what was actually shared
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    // the dashboard is behind a login and has nothing useful to searchers, so
    // ask google to leave it out entirely
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
  }, [title, description, path, noIndex]);
}
